import React, { useRef, useState, useEffect, useCallback } from "react";
import CommentForm from "components/util/helpers/CommentForm";
import { StyleTag } from "seed/helpers";

const MIN_SELECTION = 1;

const TextAnnotator = ({
  html,
  annotations = [],
  annotationNumbers = {},
  onChange,
  isEditable = true,
  currentAuthor = "",
  defaultAnnotationData = {},
}) => {

  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const draftStart = editing?.isNew ? editing.start : null;
  const draftEnd = editing?.isNew ? editing.end : null;

  const clearSelection = () => {
    const selection = window.getSelection();
    selection?.removeAllRanges();
  }

  const getHighlightTextNodes = useCallback((container) => {

    const textNodes = [];

    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          if (!node.textContent?.length) {
            return NodeFilter.FILTER_REJECT;
          }

          if (node.parentElement?.closest("[data-annotation-badge='true']")) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    return textNodes;

  }, []);

  const getTextOffset = useCallback((container, targetNode, targetOffset) => {

    const textNodes = getHighlightTextNodes(container);
    let currentOffset = 0;

    for (const node of textNodes) {

      if (targetNode === node) {
        return currentOffset + Math.max(0, Math.min(node.textContent.length, targetOffset));
      }

      const nodeRange = document.createRange();

      nodeRange.selectNodeContents(node);

      const pointPosition = nodeRange.comparePoint(targetNode, targetOffset);

      if (pointPosition === -1) {
        return currentOffset;
      }

      currentOffset += node.textContent.length;

    }

    return currentOffset;

  }, [getHighlightTextNodes]);

  const applyHighlightStyles = useCallback((span, highlight) => {

    if (highlight.id) {
      span.dataset.annotationId = highlight.id;
    }

    if (highlight.isDraft) {
      span.dataset.draftAnnotation = "true";
    }

    span.style.background = "rgba(255,230,120,0.6)";
    span.style.position = "relative";
    span.style.cursor = highlight.isDraft ? "text" : "pointer";

  }, []);

  const createAnnotationBadge = useCallback((annotationNumber) => {

    const badge = document.createElement("sup");

    badge.dataset.annotationBadge = "true";
    badge.textContent = String(annotationNumber);
    badge.style.display = "flex";
    badge.style.alignItems = "center";
    badge.style.justifyContent = "center";
    badge.style.minWidth = "16px";
    badge.style.height = "16px";
    badge.style.padding = "0 4px";
    badge.style.borderRadius = "999px";
    badge.style.background = "#f4c542";
    badge.style.color = "#5c4400";
    badge.style.fontSize = "10px";
    badge.style.fontWeight = "700";
    badge.style.position = "absolute";
    badge.style.top = "-10px";
    badge.style.right = "-10px";
    badge.style.zIndex = "2";
    badge.style.pointerEvents = "none";
    badge.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.2)";

    return badge;

  }, []);

  const wrapTextSegmentsFromOffsets = useCallback((container, highlight) => {

    const textNodes = getHighlightTextNodes(container);
    const spans = [];
    let currentOffset = 0;

    for (const node of textNodes) {

      const textLength = node.textContent.length;
      const nodeStart = currentOffset;
      const nodeEnd = currentOffset + textLength;
      const segmentStart = Math.max(highlight.start, nodeStart);
      const segmentEnd = Math.min(highlight.end, nodeEnd);

      if (segmentStart < segmentEnd) {
        const range = document.createRange();
        const span = document.createElement("span");

        applyHighlightStyles(span, highlight);

        range.setStart(node, segmentStart - nodeStart);
        range.setEnd(node, segmentEnd - nodeStart);

        try {
          range.surroundContents(span);
          spans.push(span);
        }
        catch { }
      }

      currentOffset = nodeEnd;

      if (currentOffset >= highlight.end) {
        break;
      }

    }

    return spans;

  }, [applyHighlightStyles, getHighlightTextNodes]);

  /* ------------------------------------------------ */
  /* GET RANGE VALIDATION */
  /* ------------------------------------------------ */

  const getSelectionRange = () => {

    const sel = window.getSelection();

    if (!sel || sel.rangeCount === 0)
      return null;

    const range = sel.getRangeAt(0);

    if (!contentRef.current?.contains(range.commonAncestorContainer))
      return null;

    if (range.collapsed)
      return null;

    if (range.toString().trim().length < MIN_SELECTION)
      return null;

    return range;

  };

  /* ------------------------------------------------ */
  /* GET OFFSETS RELATIVE TO TEXT */
  /* ------------------------------------------------ */

  const getOffsets = (range) => {

    const container = contentRef.current;

    if (!container) {
      return { start: 0, end: 0 };
    }

    const start = getTextOffset(container, range.startContainer, range.startOffset);
    const end = getTextOffset(container, range.endContainer, range.endOffset);

    return { start, end };

  };

  /* ------------------------------------------------ */
  /* MOUSE SELECTION */
  /* ------------------------------------------------ */

  const handleMouseUp = () => {
    if (!isEditable) return;

    const range = getSelectionRange();

    if (!range)
      return;

    const rect = range.getClientRects()[0];

    if (!rect)
      return;

    if (!containerRef.current)
      return;

    const offsets = getOffsets(range);
    const containerRect = containerRef.current.getBoundingClientRect();

    setEditing({
      text: "",
      isNew: true,
      start: offsets.start,
      end: offsets.end,
      rect: {
        top: rect.top - containerRect.top,
        left: rect.left - containerRect.left
      }
    });

  };

  /* ------------------------------------------------ */
  /* SAVE COMMENT */
  /* ------------------------------------------------ */

  const save = () => {

    if (!editing?.text?.trim())
      return;

    const id = crypto.randomUUID();
    const annotationOrder = Date.now() + Math.random();

    const next = [
      ...annotations,
      {
        id,
        text: editing.text,
        author: currentAuthor,
        ...defaultAnnotationData,
        start: editing.start,
        end: editing.end,
        order: annotationOrder
      }
    ];

    clearSelection();
    setEditing(null);
    onChange?.(next);

  };

  /* ------------------------------------------------ */
  /* DELETE COMMENT */
  /* ------------------------------------------------ */

  const remove = (id) => {
    const next = annotations.filter(a => a.id !== id);
    setEditing(null);
    onChange?.(next);
  }

  /* ------------------------------------------------ */
  /* CLICK HIGHLIGHT */
  /* ------------------------------------------------ */

  const handleClick = (e) => {
    const span = e.target.closest("[data-annotation-id]");

    if (!span)
      return;

    const id = span.dataset.annotationId;

    const ann = annotations.find(a => a.id === id);

    if (!ann)
      return;

    if (!containerRef.current)
      return;

    const rect = span.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    setEditing({
      id,
      text: ann.text,
      isNew: false,
      rect: {
        top: rect.top - containerRect.top,
        left: rect.left - containerRect.left
      }
    });

  };

  /* ------------------------------------------------ */
  /* REBUILD HIGHLIGHTS AFTER RENDER */
  /* ------------------------------------------------ */

  useEffect(() => {

    const container = contentRef.current;

    if (!container)
      return;

    /* remove previous highlights */

    container.querySelectorAll("[data-annotation-badge='true']").forEach((badge) => {
      badge.remove();
    });

    container.querySelectorAll("[data-annotation-id], [data-draft-annotation]").forEach((el) => {

      const parent = el.parentNode;

      if (!parent) {
        return;
      }

      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }

      parent.removeChild(el);

    });

    /* rebuild */

    const rangesToHighlight = annotations.map((annotation) => ({
      id: annotation.id,
      start: annotation.start,
      end: annotation.end,
      annotationNumber: annotationNumbers[annotation.id],
      isDraft: false
    }));

    if (draftStart != null && draftEnd != null) {
      rangesToHighlight.push({
        id: null,
        start: draftStart,
        end: draftEnd,
        annotationNumber: null,
        isDraft: true
      });
    }

    rangesToHighlight
      .slice()
      .sort((left, right) => {
        if (right.start !== left.start) {
          return right.start - left.start;
        }

        return right.end - left.end;
      })
      .forEach((highlight) => {

        const spans = wrapTextSegmentsFromOffsets(container, highlight);
        const badgeTarget = spans[spans.length - 1];

        if (highlight.annotationNumber && badgeTarget) {
          badgeTarget.appendChild(createAnnotationBadge(highlight.annotationNumber));
        }

      });

  }, [annotations, annotationNumbers, createAnnotationBadge, draftEnd, draftStart, html, wrapTextSegmentsFromOffsets]);

  const closeEditor = () => {

    clearSelection();
    setEditing(null);

  };

  const updateEditingText = (text) => {
    setEditing((prev) => ({
      ...prev,
      text
    }));
  };

  const saveExistingAuthor = () => {
    if (editing.isNew) {
      return save();
    }

    if (!editing?.text?.trim()) {
      return;
    }

    const next = annotations.map((annotation) => {
      if (annotation.id !== editing.id) {
        return annotation;
      }

      return {
        ...annotation,
        text: editing.text,
        author: annotation.author || currentAuthor,
      };
    });

    setEditing(null);
    onChange?.(next);
  };

  const editingWidth = 260;
  const editingStyle = editing
    ? {
      top: editing.rect.top + 25,
      left: editing.rect.left + editingWidth > containerRef.current.offsetWidth ? 
        containerRef.current.offsetWidth - editingWidth : 
        editing.rect.left,
      width: editingWidth,
      zIndex: 1000
    }
    : null;

  /* ------------------------------------------------ */
  /* RENDER */
  /* ------------------------------------------------ */

  return (

    <div
      className="text-annotator"
      ref={containerRef}
      style={{
        position: "relative",
        lineHeight: 1.7,
        wordBreak: "break-word",
        whiteSpace: "normal",
        paddingBottom: "16px"
      }}
    >
      <StyleTag content={`
        .text-annotator p {
          margin-bottom: 0.5rem;
        }
      `} />

      <div
        ref={contentRef}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
      >
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>

      <div style={{ height: "1px" }}></div>

      {editing && editingStyle && (
        <CommentForm
          isNew={editing.isNew}
          isEditable={isEditable}
          text={editing.text}
          style={editingStyle}
          onChangeText={updateEditingText}
          onRemove={() => remove(editing.id)}
          onClose={closeEditor}
          onSave={saveExistingAuthor}
        />
      )}

    </div>

  );

};

export default TextAnnotator;
